import { Button } from "@snapchat/snap-design-system";
import type { ErrorInfo, PropsWithChildren } from "react";
import { Component } from "react";
import { Flexbox } from "../Flexbox";
import { isE2eTest } from "../../extensions/configurationExtension";
import { ensureError } from "../../error/error";
import { Description, ErrorInfoCard, ErrorTitle } from "./ErrorBoundary.styles";

interface Props extends PropsWithChildren {
    showCause: boolean;
}

interface ErrorBoundaryState {
    error: Error | undefined;
}

export class ErrorBoundary extends Component<Props, ErrorBoundaryState> {
    public constructor(props) {
        super(props);
        this.state = { error: undefined };
    }

    componentDidMount() {
        window.addEventListener("unhandledrejection", this.promiseRejectionHandler);
    }

    componentWillUnmount() {
        window.removeEventListener("unhandledrejection", this.promiseRejectionHandler);
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("Uncaught error:", error, errorInfo);
        this.setState({ error });
    }

    public render() {
        if (this.state.error) {
            if (isE2eTest()) {
                return stringifyError(this.state.error);
            }
            const message = this.props.showCause
                ? `${this.state.error}`
                : "Uh oh looks like something went wrong. Please refresh the page and try again";
            return (
                <Flexbox alignItems="center" justifyContent="center">
                    <ErrorInfoCard titleBorder={false}>
                        <Flexbox alignItems="center" flexDirection="column">
                            <img src="./assets/warning.svg" />
                            <ErrorTitle>Error</ErrorTitle>
                            <Description>{message}</Description>
                            <Button type="primary" href={window.location.pathname}>
                                Refresh Page
                            </Button>
                        </Flexbox>
                    </ErrorInfoCard>
                </Flexbox>
            );
        } else {
            return this.props.children;
        }
    }

    private promiseRejectionHandler = (event: PromiseRejectionEvent) => {
        // Currently on firefox we get a bug when destroying the camerakit session.
        // This should not set the error to true since that Session is no longer in use and will
        // NEVER be used again.
        // TODO: Remove this conditional check once uncaught error gets handled
        if (event.reason?.stack.includes("LensCoreWebAssembly.wasm")) {
            return;
        }

        this.setState({
            error: event.reason,
        });
    };
}

function stringifyError(error: Error): string {
    const outer = error.stack ?? "";
    return error.cause ? `${outer}\nCaused by:\n\t${stringifyError(ensureError(error.cause))}` : outer;
}
